import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import BarraProgresso from '../components/BarraProgresso';
import './AtividadeDetalhe.css';

interface Foto {
  id: string;
  nome_original: string;
  formato: string;
  registrada_em: string;
}

interface Apontamento {
  id: string;
  atividade_id: string;
  assistente_id: string;
  percentual: number;
  registrado_em: string;
  assistente_nome: string;
  fotos?: Foto[];
}

function AtividadeDetalhe() {
  const { id: atividadeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [apontamentos, setApontamentos] = useState<Apontamento[]>([]);
  const [nomeAtividade, setNomeAtividade] = useState('');
  const [progressoAtual, setProgressoAtual] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingFoto, setDeletingFoto] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [swipeStart, setSwipeStart] = useState<number | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [editingApontamento, setEditingApontamento] = useState<string | null>(null);
  const [editPercentual, setEditPercentual] = useState(0);
  const [savingEdit, setSavingEdit] = useState(false);

  const isAssistente = user?.tipo === 'Assistente';
  // Master e Assistente podem gerenciar fotos e criar apontamentos (backend permite ambos).
  const podeGerenciar = user?.tipo === 'Assistente' || user?.tipo === 'Master';

  // Flatten all photos for lightbox navigation
  const allPhotos = apontamentos.flatMap((ap) => ap.fotos || []);

  const openLightbox = (fotoId: string) => {
    const idx = allPhotos.findIndex((f) => f.id === fotoId);
    if (idx >= 0) setLightboxIndex(idx);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    setSwipeOffset(0);
  };

  const navigateLightbox = (direction: -1 | 1) => {
    setLightboxIndex((prev) => {
      if (prev === null) return null;
      const next = prev + direction;
      if (next < 0) return allPhotos.length - 1;
      if (next >= allPhotos.length) return 0;
      return next;
    });
    setSwipeOffset(0);
  };

  // Keyboard navigation
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') navigateLightbox(-1);
      if (e.key === 'ArrowRight') navigateLightbox(1);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxIndex, allPhotos.length]);

  // Swipe handlers (touch + mouse)
  const handleSwipeStart = (x: number) => { setSwipeStart(x); setSwipeOffset(0); };
  const handleSwipeMove = (x: number) => { if (swipeStart !== null) setSwipeOffset(x - swipeStart); };
  const handleSwipeEnd = () => {
    if (swipeStart !== null) {
      if (swipeOffset < -60) navigateLightbox(1);
      else if (swipeOffset > 60) navigateLightbox(-1);
      setSwipeStart(null);
      setSwipeOffset(0);
    }
  };

  const startEditPercentual = (apontamento: Apontamento) => {
    setEditingApontamento(apontamento.id);
    setEditPercentual(apontamento.percentual);
  };

  const saveEditPercentual = async () => {
    if (!editingApontamento) return;
    setSavingEdit(true);
    try {
      await api.patch(`/apontamentos/${editingApontamento}`, { percentual: editPercentual });
      setEditingApontamento(null);
      await loadData();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao editar percentual');
    } finally {
      setSavingEdit(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data: Apontamento[] = await api.get(`/atividades/${atividadeId}/apontamentos`);

      if (data && data.length > 0) {
        setNomeAtividade((data[0] as Apontamento & { atividade_nome?: string }).atividade_nome || 'Atividade');
        setProgressoAtual(data[0].percentual);
      }

      // Load photos for each apontamento
      const apontamentosComFotos = await Promise.all(
        data.map(async (apontamento) => {
          try {
            const fotos: Foto[] = await api.get(`/apontamentos/${apontamento.id}/fotos`);
            return { ...apontamento, fotos };
          } catch {
            return { ...apontamento, fotos: [] };
          }
        })
      );

      setApontamentos(apontamentosComFotos);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [atividadeId]);

  useEffect(() => {
    if (atividadeId) loadData();
  }, [atividadeId, loadData]);

  const handleDeleteFoto = async (fotoId: string) => {
    if (confirmDelete !== fotoId) {
      setConfirmDelete(fotoId);
      return;
    }

    try {
      setDeletingFoto(fotoId);
      await api.delete(`/fotos/${fotoId}`);
      // Remove photo from local state
      setApontamentos((prev) =>
        prev.map((ap) => ({
          ...ap,
          fotos: ap.fotos?.filter((f) => f.id !== fotoId),
        }))
      );
      setConfirmDelete(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir foto');
    } finally {
      setDeletingFoto(null);
    }
  };

  const cancelDelete = () => {
    setConfirmDelete(null);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="atividade-detalhe">
        <div className="atividade-detalhe-container">
          <p style={{ color: '#c9a96e', textAlign: 'center', marginTop: '2rem' }}>
            Carregando histórico...
          </p>
        </div>
      </div>
    );
  }

  if (error && apontamentos.length === 0) {
    return (
      <div className="atividade-detalhe">
        <div className="atividade-detalhe-container">
          <p style={{ color: '#ff6b6b', textAlign: 'center', marginTop: '2rem' }}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="atividade-detalhe">
      <div className="atividade-detalhe-container">
        <button className="atividade-detalhe-back" onClick={() => navigate(-1)}>
          ← Voltar
        </button>

        <header className="atividade-detalhe-header">
          <h1>{nomeAtividade || 'Atividade'}</h1>
          <div className="atividade-detalhe-progresso">
            <span className="progresso-label">Progresso atual:</span>
            <div className="progresso-bar-wrapper">
              <BarraProgresso value={progressoAtual} />
            </div>
            <span className="progresso-valor">{progressoAtual}%</span>
          </div>
        </header>

        {isAssistente && (
          <button
            className="btn-novo-apontamento"
            onClick={() => navigate(`/admin/atividades/${atividadeId}/apontamento`)}
          >
            📝 Novo Apontamento
          </button>
        )}

        {error && (
          <p style={{ color: '#ff6b6b', marginBottom: '1rem', fontSize: '0.85rem' }}>{error}</p>
        )}

        <section className="historico-section" aria-label="Histórico de apontamentos">
          <h2>Histórico de Apontamentos</h2>

          {apontamentos.length === 0 ? (
            <p className="historico-vazio">Nenhum apontamento registrado.</p>
          ) : (
            <div className="historico-timeline">
              {apontamentos.map((apontamento) => (
                <div className="apontamento-card" key={apontamento.id}>
                  <div className="apontamento-card-header">
                    {editingApontamento === apontamento.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={editPercentual}
                          onChange={(e) => setEditPercentual(Math.min(100, Math.max(0, Number(e.target.value))))}
                          style={{ width: '60px', padding: '0.3rem', borderRadius: '4px', border: '1px solid #c9a96e', background: '#1a1a1a', color: '#fff', textAlign: 'center' }}
                        />
                        <span style={{ color: '#999', fontSize: '0.85rem' }}>%</span>
                        <button
                          className="btn-apontamento"
                          onClick={saveEditPercentual}
                          disabled={savingEdit}
                          style={{ padding: '0.3rem 0.6rem', fontSize: '0.75rem', minHeight: '32px' }}
                        >
                          {savingEdit ? '...' : '✓'}
                        </button>
                        <button
                          style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '0.85rem', minHeight: '32px', minWidth: '32px' }}
                          onClick={() => setEditingApontamento(null)}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <span className="apontamento-percentual-badge">
                        {apontamento.percentual}%
                      </span>
                    )}
                    <div className="apontamento-meta">
                      <span className="apontamento-data">
                        {formatDate(apontamento.registrado_em)}
                      </span>
                      <span className="apontamento-responsavel">
                        por {apontamento.assistente_nome}
                      </span>
                    </div>
                    {isAssistente && user?.id === apontamento.assistente_id && editingApontamento !== apontamento.id && (
                      <button
                        onClick={() => startEditPercentual(apontamento)}
                        style={{ background: 'none', border: '1px solid #4d4d4d', borderRadius: '4px', color: '#c9a96e', cursor: 'pointer', padding: '0.3rem 0.5rem', fontSize: '0.75rem', minHeight: '32px', marginLeft: 'auto' }}
                        aria-label="Editar percentual"
                      >
                        ✏️ Editar
                      </button>
                    )}
                  </div>

                  <div className="apontamento-fotos-section">
                    {apontamento.fotos && apontamento.fotos.length > 0 ? (
                      <div className="fotos-grid">
                        {apontamento.fotos.map((foto) => (
                          <div className="foto-thumbnail-wrapper" key={foto.id}>
                            <img
                              src={`/api/fotos/${foto.id}/url`}
                              alt={foto.nome_original}
                              className="foto-thumbnail"
                              loading="lazy"
                              onClick={() => openLightbox(foto.id)}
                              style={{ cursor: 'pointer' }}
                            />
                            {podeGerenciar && (
                              <button
                                className={`foto-delete-btn ${confirmDelete === foto.id ? 'confirming' : ''}`}
                                onClick={(e) => { e.stopPropagation(); handleDeleteFoto(foto.id); }}
                                onBlur={cancelDelete}
                                disabled={deletingFoto === foto.id}
                                aria-label={
                                  confirmDelete === foto.id
                                    ? `Confirmar exclusão de ${foto.nome_original}`
                                    : `Excluir ${foto.nome_original}`
                                }
                                title={confirmDelete === foto.id ? 'Clique novamente para confirmar' : 'Excluir foto'}
                              >
                                {deletingFoto === foto.id ? '...' : confirmDelete === foto.id ? '✓' : '✕'}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="sem-fotos">Sem fotos</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Lightbox com swipe */}
        {lightboxIndex !== null && allPhotos[lightboxIndex] && (
          <div className="lightbox-overlay" onClick={closeLightbox}>
            <div
              className="lightbox-content"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={(e) => handleSwipeStart(e.touches[0].clientX)}
              onTouchMove={(e) => handleSwipeMove(e.touches[0].clientX)}
              onTouchEnd={handleSwipeEnd}
              onMouseDown={(e) => handleSwipeStart(e.clientX)}
              onMouseMove={(e) => { if (swipeStart !== null) handleSwipeMove(e.clientX); }}
              onMouseUp={handleSwipeEnd}
              onMouseLeave={handleSwipeEnd}
            >
              <button className="lightbox-close" onClick={closeLightbox} aria-label="Fechar">✕</button>
              <button className="lightbox-nav lightbox-prev" onClick={() => navigateLightbox(-1)} aria-label="Anterior">‹</button>
              <div className="lightbox-image-wrapper" style={{ transform: `translateX(${swipeOffset}px)`, transition: swipeStart ? 'none' : 'transform 0.3s ease' }}>
                <img
                  src={`/api/fotos/${allPhotos[lightboxIndex].id}/url`}
                  alt={allPhotos[lightboxIndex].nome_original}
                  className="lightbox-image"
                  draggable={false}
                />
              </div>
              <button className="lightbox-nav lightbox-next" onClick={() => navigateLightbox(1)} aria-label="Próxima">›</button>
              <div className="lightbox-counter">{lightboxIndex + 1} / {allPhotos.length}</div>
              <div className="lightbox-dots">
                {allPhotos.map((_, i) => (
                  <span key={i} className={`lightbox-dot ${i === lightboxIndex ? 'active' : ''}`} onClick={() => { setLightboxIndex(i); setSwipeOffset(0); }} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AtividadeDetalhe;
